/**
 * API Test Page - Test connection to production API
 */

import { useState, useEffect } from 'react'
import { apiClient } from '../services/apiClient'
import { nodeServiceApi } from '../services/nodeServiceApi'

export function ApiTest() {
  const [healthStatus, setHealthStatus] = useState<any>(null)
  const [nodes, setNodes] = useState<any[]>([])
  const [loading, setLoading] = useState(true)
  const [error, setError] = useState<string | null>(null)

  useEffect(() => {
    testApi()
  }, [])

  async function testApi() {
    setLoading(true)
    setError(null)

    try {
      // Test health endpoint
      console.log('Testing API health...')
      const health = await apiClient.healthCheck()
      setHealthStatus(health)
      console.log('Health check OK:', health)

      // Test nodes endpoint
      console.log('Fetching nodes...')
      const nodesData = await nodeServiceApi.getAll()
      setNodes(nodesData)
      console.log(`Fetched ${nodesData.length} nodes`)
    } catch (err: any) {
      console.error('API Test failed:', err)
      setError(err.message)
    } finally {
      setLoading(false)
    }
  }

  async function createTestNode() {
    try {
      const testNode = {
        tipo: 'NAP' as const,
        nombre: `Test NAP ${Date.now()}`,
        descripcion: 'Created from frontend test',
        lat: 4.6097,
        lng: -74.0817,
      }

      console.log('Creating test node...', testNode)
      const created = await nodeServiceApi.create(testNode)
      console.log('Node created:', created)

      // Refresh nodes list
      await testApi()
      alert('Nodo creado exitosamente!')
    } catch (err: any) {
      console.error('Failed to create node:', err)
      alert(`Error: ${err.message}`)
    }
  }

  return (
    <div className="p-8 max-w-4xl mx-auto">
      <h1 className="text-3xl font-bold mb-6">NetEasyFTTX - API Connection Test</h1>

      {/* API Configuration */}
      <div className="mb-6 p-4 bg-gray-100 rounded-lg">
        <h2 className="text-xl font-semibold mb-2">API Configuration</h2>
        <div className="space-y-1 text-sm">
          <p>
            <strong>Base URL:</strong> {import.meta.env.VITE_API_URL || 'https://neteasy.wispchat.net'}
          </p>
          <p>
            <strong>Tenant:</strong> {apiClient.getTenantSlug()}
          </p>
        </div>
      </div>

      {/* Loading State */}
      {loading && (
        <div className="p-4 bg-blue-100 border border-blue-400 rounded">
          <p>⏳ Testing API connection...</p>
        </div>
      )}

      {/* Error State */}
      {error && (
        <div className="p-4 bg-red-100 border border-red-400 rounded mb-4">
          <h3 className="font-semibold text-red-800">❌ Error</h3>
          <p className="text-red-700">{error}</p>
        </div>
      )}

      {/* Health Status */}
      {healthStatus && (
        <div className="mb-6 p-4 bg-green-100 border border-green-400 rounded">
          <h2 className="text-xl font-semibold mb-2 text-green-800">✅ API Health Check</h2>
          <pre className="text-sm bg-white p-2 rounded overflow-auto">
            {JSON.stringify(healthStatus, null, 2)}
          </pre>
        </div>
      )}

      {/* Nodes List */}
      <div className="mb-6 p-4 bg-white border border-gray-300 rounded">
        <div className="flex justify-between items-center mb-4">
          <h2 className="text-xl font-semibold">Nodes ({nodes.length})</h2>
          <button
            onClick={createTestNode}
            className="px-4 py-2 bg-blue-500 text-white rounded hover:bg-blue-600"
          >
            Create Test Node
          </button>
        </div>

        {nodes.length === 0 ? (
          <p className="text-gray-500">No nodes found. Create one to test!</p>
        ) : (
          <div className="space-y-2">
            {nodes.map((node: any) => (
              <div key={node.id} className="p-3 bg-gray-50 rounded border">
                <div className="flex justify-between">
                  <div>
                    <p className="font-semibold">{node.nombre || 'Unnamed'}</p>
                    <p className="text-sm text-gray-600">
                      Type: {node.tipo} | ID: {node.id}
                    </p>
                    <p className="text-sm text-gray-600">
                      Coords: ({node.lat.toFixed(4)}, {node.lng.toFixed(4)})
                    </p>
                  </div>
                  <div className="text-xs text-gray-500">
                    Created: {new Date(node.createdAt).toLocaleString()}
                  </div>
                </div>
              </div>
            ))}
          </div>
        )}
      </div>

      {/* Actions */}
      <div className="flex gap-4">
        <button
          onClick={testApi}
          className="px-4 py-2 bg-gray-500 text-white rounded hover:bg-gray-600"
        >
          🔄 Refresh
        </button>
        <a
          href="https://neteasy.wispchat.net/health"
          target="_blank"
          rel="noopener noreferrer"
          className="px-4 py-2 bg-green-500 text-white rounded hover:bg-green-600"
        >
          🌐 Open API Health
        </a>
        <a href="/" className="px-4 py-2 bg-purple-500 text-white rounded hover:bg-purple-600">
          ← Back to App
        </a>
      </div>
    </div>
  )
}
